import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import SubscribeModal from "@/components/SubscribeModal";
import ManageBilling from "@/components/ManageBilling";
import api from "@/lib/api";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [subscribe, setSubscribe] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    api.get("/pricing").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (searchParams.get("checkout") === "canceled") {
      toast.info("Checkout canceled — no charge was made.");
      searchParams.delete("checkout");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div>
      <PageHeader
        overline="Pricing"
        title="Simple, transparent pricing."
        subtitle="Choose a package that fits your business. Every project is custom-built, fully responsive, and optimized to convert."
      />

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 gap-6 max-w-md mx-auto">
          {plans.map((plan, i) => {
            const featured = plan.highlighted;
            return (
              <Reveal key={plan.name} delay={i * 0.08}>
                <div
                  data-testid={`pricing-plan-${plan.name.toLowerCase()}`}
                  className={`h-full flex flex-col border p-8 sm:p-10 ${
                    featured ? "bg-black text-white border-black" : "bg-white border-black/10"
                  }`}
                >
                  {featured && (
                    <div className="inline-block self-start text-[11px] uppercase tracking-[0.2em] bg-white text-black px-3 py-1 rounded-full mb-6">
                      Most Popular
                    </div>
                  )}
                  <h3 className="font-display text-2xl font-bold tracking-tight">{plan.name}</h3>
                  <p className={`mt-2 text-sm ${featured ? "text-zinc-400" : "text-zinc-500"}`}>{plan.tagline}</p>
                  <div className="mt-6 flex items-end gap-1">
                    <span className="font-display text-5xl font-black tracking-tighter">${(plan.price ?? 0).toLocaleString()}</span>
                    <span className={`mb-1.5 text-sm ${featured ? "text-zinc-400" : "text-zinc-500"}`}>setup</span>
                  </div>
                  {plan.monthly_price > 0 && (
                    <div className={`mt-1 text-sm ${featured ? "text-zinc-400" : "text-zinc-500"}`}>
                      then <span className="font-semibold text-current">${plan.monthly_price.toLocaleString()}/mo</span> maintenance
                    </div>
                  )}
                  <ul className="mt-8 space-y-3 flex-1">
                    {(plan.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <Check size={18} weight="bold" className={featured ? "text-white shrink-0 mt-0.5" : "text-black shrink-0 mt-0.5"} />
                        <span className={featured ? "text-zinc-200" : "text-zinc-700"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-10 space-y-3">
                    <button
                      type="button"
                      data-testid={`pricing-subscribe-${plan.name.toLowerCase()}`}
                      onClick={() => setSubscribe(plan)}
                      className={`w-full inline-flex items-center justify-center gap-2 rounded-full font-medium px-6 py-3.5 transition-colors ${
                        featured ? "bg-white text-black hover:bg-zinc-200" : "bg-black text-white hover:bg-zinc-800"
                      }`}
                    >
                      Get Started <ArrowRight size={18} />
                    </button>
                    <p className={`text-xs text-center ${featured ? "text-zinc-400" : "text-zinc-500"}`}>
                      ${(plan.price ?? 0).toLocaleString()} setup today{plan.monthly_price > 0 ? <>, then ${plan.monthly_price.toLocaleString()}/mo · cancel anytime</> : null}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <p className="mt-12 text-center text-zinc-500">
          Have a referral code? Enter it at checkout for an extra <b>10% off</b> your setup fee.
        </p>

        <div className="mt-16">
          <ManageBilling />
        </div>
      </section>

      {subscribe && <SubscribeModal plan={subscribe} onClose={() => setSubscribe(null)} />}
    </div>
  );
}
